/**
 * Whether the bot answers its own direct messages, and who signs off.
 *
 * Lives with Run / Pause / Stop rather than on a settings page, because it is the
 * same kind of control: it decides what the persona does when nobody is watching.
 * §7.3 keeps those always visible and never behind a menu.
 *
 * Three states, not a switch. "Manual" is a real choice — the inbox someone reads
 * and types into — and collapsing it into "off" would make turning automation off
 * look like turning DM off.
 *
 * Gated on `dm`, not on `read_dm`: this control decides whether the bot *sends*.
 * A bot on a platform with no DM adapter resolves `dm` absent under §7.4 rule 4
 * and the whole control disappears, which is right — there is nothing to
 * automate.
 */

import { useState } from 'react';
import { DM_REPLY_MODE, DM_REPLY_MODE_COPY, type DmReplyMode } from '@pm/shared';

import { apiFetch } from '../../api/client';
import { routes } from '../../api/routes';
import type { Bot } from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { Gate } from '../../components/Gate';
import { InlineError } from '../../components/ErrorView';
import type { Problem } from '../../api/problem';
import { ProblemError } from '../../api/problem';

export function DmReplyModeControl({
  bot,
  caps,
  reload,
}: {
  bot: Bot;
  caps: CapabilityResolver;
  reload: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const change = async (mode: DmReplyMode) => {
    if (mode === bot.dm_reply_mode) return;
    setPending(true);
    setProblem(null);
    try {
      await apiFetch(routes.bots.one(bot.id), {
        method: 'PATCH',
        body: { dm_reply_mode: mode },
      });
      reload();
    } catch (err) {
      setProblem(err instanceof ProblemError ? err.problem : null);
    } finally {
      setPending(false);
    }
  };

  return (
    <Gate caps={caps} capability="dm">
      {(disabled, reason) => (
        <div className="dm-mode">
          <fieldset disabled={disabled || pending}>
            <legend>Direct message replies</legend>
            <div className="dm-mode-options">
              {DM_REPLY_MODE.map((mode) => {
                const copy = DM_REPLY_MODE_COPY[mode];
                return (
                  <label key={mode} className="dm-mode-option">
                    <input
                      type="radio"
                      name={`dm-mode-${bot.id}`}
                      value={mode}
                      checked={bot.dm_reply_mode === mode}
                      onChange={() => void change(mode)}
                    />
                    <span>
                      <strong>{copy.label}</strong>
                      <span className="muted">{copy.detail}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/*
            Stated on the control rather than left to be discovered. Automation
            does not loosen any DM rule, and someone switching this on should not
            find that out by watching replies fail.
          */}
          {bot.dm_reply_mode !== 'off' && !reason ? (
            <p className="muted">
              The bot still refuses threads the other person did not start, and stops at the
              daily caps. Every reply is generated and billed like any other generation.
            </p>
          ) : null}

          {problem ? <InlineError problem={problem} /> : null}
        </div>
      )}
    </Gate>
  );
}
